import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import useSeo from "../hooks/useSeo";
import useEnforceLightMode from "../hooks/useEnforceLightMode";
import { GUIDES, getGuideBySlug, guideJsonLd } from "../seo/guides";
import { breadcrumbJsonLd, faqJsonLd } from "../seo/site";

const updatedLabel = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export default function GuidePage() {
  useEnforceLightMode();
  const { slug } = useParams<{ slug: string }>();
  const guide = slug ? getGuideBySlug(slug) : undefined;
  const path = `/guides/${slug ?? ""}`;

  useSeo(
    guide
      ? {
          title: `${guide.title} | DealHawk`,
          description: guide.description,
          path,
          type: "article",
          jsonLd: [
            guideJsonLd(guide, path),
            faqJsonLd(guide.faq),
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Guides", path: "/guides" },
              { name: guide.retailerName },
            ]),
          ],
        }
      : null,
  );

  if (!guide) return <Navigate to="/guides" replace />;
  const related = GUIDES.filter((g) => g.slug !== guide.slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-16">
        <nav aria-label="Breadcrumb" className="text-sm text-slate-500 mb-6">
          <Link to="/" className="hover:text-violet-700">Home</Link>
          <span className="mx-2">›</span>
          <Link to="/guides" className="hover:text-violet-700">Guides</Link>
          <span className="mx-2">›</span>
          <span>{guide.retailerName}</span>
        </nav>

        <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">{guide.h1}</h1>
        <p className="mt-3 text-sm text-slate-500">Updated {updatedLabel(guide.updated)}</p>
        <p className="mt-6 text-lg leading-relaxed text-slate-700">{guide.intro}</p>

        {guide.sections.map((section) => (
          <section key={section.heading} className="mt-10">
            <h2 className="text-2xl font-bold">{section.heading}</h2>
            {section.paragraphs?.map((p) => (
              <p key={p} className="mt-4 leading-relaxed text-slate-700">{p}</p>
            ))}
            {section.bullets && (
              <ul className="mt-4 space-y-2 list-disc pl-6 text-slate-700">
                {section.bullets.map((b) => (
                  <li key={b} className="leading-relaxed">{b}</li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {guide.pennyMeals && (
          <aside className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
            <h2 className="text-lg font-bold">{guide.pennyMeals.heading}</h2>
            <p className="mt-2 leading-relaxed text-slate-700">
              {guide.pennyMeals.body.map((part, i) =>
                typeof part === "string" ? (
                  part
                ) : (
                  <a key={i} href={part.href} className="font-semibold text-violet-700 underline hover:text-violet-800">
                    {part.text}
                  </a>
                ),
              )}
            </p>
          </aside>
        )}

        <div className="mt-10 flex flex-wrap gap-3">
          {guide.dealsPath && (
            <Link
              to={guide.dealsPath}
              className="inline-flex items-center gap-2 rounded-xl bg-violet-700 px-5 py-3 font-semibold text-white hover:bg-violet-800"
            >
              See live {guide.retailerName} deals <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          {guide.shopPath && (
            <a
              href={guide.shopPath}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3 font-semibold hover:border-violet-300"
            >
              {guide.retailerName} price drops & history
            </a>
          )}
          <Link
            to={guide.featurePath}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3 font-semibold hover:border-violet-300"
          >
            How DealHawk works
          </Link>
        </div>

        <section className="mt-14">
          <h2 className="text-2xl font-bold">Frequently asked questions</h2>
          <div className="mt-4 divide-y divide-slate-200 border-y border-slate-200">
            {guide.faq.map((f) => (
              <div key={f.q} className="py-5">
                <h3 className="font-semibold">{f.q}</h3>
                <p className="mt-2 leading-relaxed text-slate-700">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-2xl bg-[#3A075C] p-6 sm:p-8 text-white">
          <h2 className="text-2xl font-bold">Find the markdowns before they're gone</h2>
          <p className="mt-2 text-violet-100">
            DealHawk tracks clearance and price drops at the stores near you and alerts you when prices fall.
          </p>
          <Link
            to="/get-started"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 font-semibold text-[#3A075C]"
          >
            Start free <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        <section className="mt-14">
          <h2 className="text-xl font-bold">More clearance guides</h2>
          <ul className="mt-4 grid sm:grid-cols-2 gap-3">
            {related.map((g) => (
              <li key={g.slug}>
                <Link
                  to={`/guides/${g.slug}`}
                  className="flex h-full items-start gap-3 rounded-xl border border-slate-200 p-4 hover:border-violet-300"
                >
                  <BookOpen className="w-5 h-5 mt-0.5 text-violet-700 flex-shrink-0" />
                  <span className="font-semibold">{g.h1}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </article>
    </div>
  );
}
